/**
 * A game's human history on Match Administration (Ben, 2026-10-22): who
 * marked it reviewed, and Priori staff notes added later — "Ben approves
 * the game, and later John adds commentary". One timeline, newest first.
 *
 * Staff-only (the server enforces it; a club never sees these). A note is
 * never edited — a correction is a new note — and its author can withdraw
 * it, which keeps it in the timeline marked withdrawn.
 */
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'

type Note = {
  id: number; body: string; by: string | null; at: string | null
  removed: boolean; removed_by: string | null; removed_at: string | null; mine: boolean
}
type ReviewChange = { reviewed: boolean; by: string | null; at: string | null }

type Item =
  | { kind: 'note'; at: string | null; note: Note }
  | { kind: 'review'; at: string | null; change: ReviewChange }

// The server's limit (MAX_NOTE_CHARS); it refuses longer, this just says so first.
const MAX_CHARS = 4000

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

const msg = (e: unknown) =>
  e instanceof ApiError || e instanceof Error ? e.message : String(e)

export function GameNotes({ gameId }: { gameId: number }) {
  const { log, stackVersion } = useActionLog()
  const [notes, setNotes] = useState<Note[] | null>(null)
  const [total, setTotal] = useState(0)
  const [reviews, setReviews] = useState<ReviewChange[]>([])
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    api<{ notes: Note[]; total?: number }>(`/api/v1/opsqa/games/${gameId}/notes`)
      .then((d) => {
        const list = Array.isArray(d?.notes) ? d.notes : []
        setNotes(list)
        setTotal(typeof d?.total === 'number' ? d.total : list.length)
        setError(null)
      })
      .catch((e: unknown) => setError(msg(e)))
    // The review history is the other half of the timeline; without it the
    // notes still show.
    api<{ history?: ReviewChange[] }>(`/api/v1/opsqa/games/${gameId}/review`)
      .then((d) => setReviews(Array.isArray(d?.history) ? d.history : []))
      .catch(() => setReviews([]))
  }, [gameId])

  // stackVersion moves after any logged action — a Human reviewed check
  // among them — so the timeline follows the box beside it.
  useEffect(() => { load() }, [load, stackVersion])

  const add = async () => {
    const body = draft.trim()
    if (!body) return
    setSaving(true)
    try {
      await api(`/api/v1/opsqa/games/${gameId}/notes`,
        { method: 'POST', body: JSON.stringify({ body }) })
      setDraft('')
      log('info', 'Added a note to this match')
      load()
    } catch (e: unknown) {
      log('fail', `Note not saved — ${msg(e)}`)
    } finally {
      setSaving(false)
    }
  }

  const withdraw = async (note: Note) => {
    if (!window.confirm('Withdraw this note? It stays in the history, marked withdrawn.')) return
    try {
      await api(`/api/v1/opsqa/games/${gameId}/notes/${note.id}`, { method: 'DELETE' })
      log('info', 'Withdrew a note on this match')
      load()
    } catch (e: unknown) {
      log('fail', `Note not withdrawn — ${msg(e)}`)
    }
  }

  const items: Item[] = [
    ...(notes ?? []).map((n): Item => ({ kind: 'note', at: n.at, note: n })),
    ...reviews.map((c): Item => ({ kind: 'review', at: c.at, change: c })),
  ].sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''))
  const live = (notes ?? []).filter((n) => !n.removed).length
  const tooLong = draft.trim().length > MAX_CHARS

  return (
    <section className="ls-notes" data-testid="game-notes">
      <div className="ls-notes__head">
        <span className="t-label">Notes &amp; review</span>
        {/* no count until the notes are read: "0 notes" above "could not
            be read" would contradict itself */}
        <span className="muted">
          {notes !== null && <>{live} note{live === 1 ? '' : 's'} · </>}Priori staff only
        </span>
      </div>
      {error && <p className="error">Notes could not be read — {error}</p>}
      <div className="ls-notes__add">
        <textarea className="adm-input" rows={2} value={draft}
                  aria-label="Add a note to this match"
                  placeholder="Add a note — what you checked, what looks off, context for the next person"
                  onChange={(e) => setDraft(e.target.value)} />
        <button type="button" className="adm-btn"
                disabled={saving || !draft.trim() || tooLong}
                onClick={() => void add()}>
          {saving ? 'Saving…' : 'Add note'}
        </button>
      </div>
      {tooLong && <p className="error">A note is at most {MAX_CHARS} characters.</p>}
      {items.length === 0 && notes !== null && (
        <p className="muted">No notes or reviews yet.</p>
      )}
      <ol className="ls-notes__list">
        {items.map((it) => it.kind === 'review' ? (
          <li key={`r-${it.at}-${String(it.change.reviewed)}`} className="ls-notes__review">
            <strong>{it.change.reviewed ? '✓ Marked reviewed' : 'Unmarked as reviewed'}</strong>
            {' '}by {it.change.by ?? 'unknown'} · {when(it.at)}
          </li>
        ) : (
          <li key={`n-${it.note.id}`} className={it.note.removed ? 'is-removed' : undefined}>
            <div className="ls-notes__meta">
              <strong>{it.note.by ?? 'unknown'}</strong> · {when(it.at)}
              {it.note.removed && (
                <span className="muted"> · withdrawn by {it.note.removed_by ?? 'unknown'} {when(it.note.removed_at)}</span>
              )}
              {it.note.mine && !it.note.removed && (
                <>
                  {' '}
                  <button type="button" className="linklike" onClick={() => void withdraw(it.note)}>
                    withdraw
                  </button>
                </>
              )}
            </div>
            <div className="ls-notes__body">{it.note.body}</div>
          </li>
        ))}
      </ol>
      {notes !== null && total > notes.length && (
        <p className="muted">… and {total - notes.length} earlier note{total - notes.length === 1 ? '' : 's'}</p>
      )}
    </section>
  )
}
